/**
 * PageCard
 * Card component for displaying page information with actions.
 */

import { useState } from 'react'
import {
  Home,
  MoreHorizontal,
  Pencil,
  ExternalLink,
  Trash2,
  Settings,
  Copy,
  type LucideIcon,
} from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  useTogglePagePublished,
  useDeletePage,
  useSetHomePage,
  useDuplicatePage,
} from '@/hooks/use-pages'
import { cn } from '@/lib/utils'
import { PageSettingsDialog } from './PageSettingsDialog'
import type { PageWithSectionCount } from '@/hooks/use-pages'
import type { DynamicPageType } from '@limo/shared'

interface PageCardProps {
  page: PageWithSectionCount
  isHomePage: boolean
  onEdit: () => void
  getPreviewUrl: (slug: string) => string
  getPageIcon: (pageType: DynamicPageType) => LucideIcon
  basePath: string
}

export function PageCard({
  page,
  isHomePage,
  onEdit,
  getPreviewUrl,
  getPageIcon,
}: PageCardProps) {
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)

  const togglePublished = useTogglePagePublished()
  const deletePage = useDeletePage()
  const setHomePage = useSetHomePage()
  const duplicatePage = useDuplicatePage()

  const Icon = getPageIcon(page.page_type as DynamicPageType)
  const previewUrl = getPreviewUrl(page.slug)

  const handleToggle = (checked: boolean) => {
    togglePublished.mutate({
      pageId: page.id,
      isPublished: checked,
    })
  }

  const handleSetAsHome = () => {
    setHomePage.mutate({
      accountId: page.account_id,
      pageId: page.id,
    })
  }

  const handleDelete = () => {
    deletePage.mutate({
      pageId: page.id,
      accountId: page.account_id,
    })
  }

  const handleDuplicate = () => {
    duplicatePage.mutate({
      pageId: page.id,
      accountId: page.account_id,
    })
  }

  return (
    <Card
      className={cn(
        'transition-all hover:shadow-md',
        !page.is_published && 'opacity-60'
      )}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'rounded-lg p-2',
                page.is_published ? 'bg-primary/10' : 'bg-muted'
              )}
            >
              <Icon
                className={cn(
                  'h-6 w-6',
                  page.is_published ? 'text-primary' : 'text-muted-foreground'
                )}
              />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {page.title}
                {isHomePage && (
                  <Badge
                    variant="secondary"
                    className="text-xs font-normal flex items-center gap-1"
                  >
                    <Home className="h-3 w-3" />
                    Home
                  </Badge>
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                /{page.slug || '(root)'}
              </p>
              {!page.is_published && (
                <span className="text-xs text-muted-foreground">
                  Hidden from website
                </span>
              )}
            </div>
          </div>

          {/* Toggle switch in top right */}
          <div className="flex items-center gap-2">
            <Label htmlFor={`toggle-${page.id}`} className="sr-only">
              {page.is_published ? 'Unpublish' : 'Publish'} {page.title}
            </Label>
            <Switch
              id={`toggle-${page.id}`}
              checked={page.is_published}
              onCheckedChange={handleToggle}
              disabled={togglePublished.isPending}
            />
          </div>
        </div>
        <CardDescription className="mt-2">
          {page.section_count}{' '}
          {page.section_count === 1 ? 'section' : 'sections'}
        </CardDescription>
      </CardHeader>

      <CardFooter className="flex gap-2">
        <Button onClick={onEdit} className="flex-1">
          Edit Sections
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">More options</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit Page
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="mr-2 h-4 w-4" />
              Page Settings
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={handleDuplicate}
              disabled={duplicatePage.isPending}
            >
              <Copy className="mr-2 h-4 w-4" />
              {duplicatePage.isPending ? 'Duplicating...' : 'Duplicate Page'}
            </DropdownMenuItem>

            {!isHomePage && (
              <DropdownMenuItem onClick={handleSetAsHome}>
                <Home className="mr-2 h-4 w-4" />
                Set as Home Page
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => window.open(previewUrl, '_blank')}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Preview
            </DropdownMenuItem>

            {!isHomePage && (
              <>
                <DropdownMenuSeparator />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Page
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Page</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{page.title}"? This
                        action cannot be undone and all content will be
                        permanently lost.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardFooter>

      <PageSettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
        page={page}
      />
    </Card>
  )
}
